<script setup lang="ts">

import {computed, onMounted, ref} from "vue";
import {FindOne, UpdateOne, UrlQuery, useWebPopup, WebEditor, WebInput} from "lct-lib";
import {useCartStore} from "../cart.store";
import _ from "lodash";
import axios from "axios";

const {show, hide} = useWebPopup();
const orderId = ref<any>(0);
const email = ref("");
const order = ref();
const isDraft = ref(false);
const isFromUrl = ref(false);
const ask = ref("");
const {addItem} = useCartStore();

const canApproveProof = computed(() => {
    if (!order.value || isDraft.value) return false;
    return ["Proof Sent", "Waiting for Proof Approval"].includes(order.value.status);
});

onMounted(async () => {
    orderId.value = Number(UrlQuery("id"));
    email.value = <string>UrlQuery("e");
    isDraft.value = !!(UrlQuery("draft") && UrlQuery("draft") === "1");

    isFromUrl.value = !!UrlQuery("id") && !!UrlQuery("e");

    if (orderId.value && email.value) {
        await getOrder();
    }
})

const getOrder = async () => {
    try {
        if(!order.value && !email.value) return;
        show();
        const result = await FindOne("orders", {_id: Number(orderId.value), $or: [{email: email.value}, {"customer.email": email.value}]});
        order.value = result;
        hide();
    } catch (e) {
        console.log(e)
    }
}

const addDraftToCart = async () => {
    for (const item of order.value.items) {
        if (item.sale_price === "99.99" || item.sale_price === 99.99) {
            item.sale_price = "69.99"
        }
        if (item.sale_price === "13.99" || item.sale_price === 13.99) {
            item.sale_price = "3.99"
        }
        addItem(item);
    }
    show("You have successfully added it to your cart. <br/> Please proceed to checkout.", ()=>{
        location.href = "/cart";
    })

}

const submitAsk = async () => {
    if(!ask.value) return;
    try {
        show();
        const result = await UpdateOne("orders", {_id: order.value._id}, {
            $inc: {
                admin_unread_asks_count: 1,
            },
            $push: {
                asks: {
                    ask: ask.value,
                    created_at: new Date(),
                    who: "customer",
                }
            }
        }, {upsert: true});

        const sendMail = await axios.post('https://sv.lct.vn/emails/update-your-order/send-admin', {id: order.value._id});

        ask.value = "";

        show("Your ask has been submitted. <br/> We will contact you as soon as possible.", ()=>{
            location.href = "/order-detail?id=" + order.value._id + "&e=" + order.value.email;
        })
    } catch (e) {
        console.log(e)
    }
}

const approveProof = async () => {
    if (!order.value || !canApproveProof.value) return;

    try {
        show();
        const approvedAt = new Date();
        await UpdateOne("orders", {_id: order.value._id}, {
            $set: {
                status: "Proof Approved",
                proof_approved_at: approvedAt,
            },
            $inc: {
                admin_unread_asks_count: 1,
            },
            $push: {
                asks: {
                    ask: "I approve this proof.",
                    created_at: approvedAt,
                    who: "customer",
                }
            }
        }, {upsert: true});

        order.value.status = "Proof Approved";
        order.value.proof_approved_at = approvedAt;
        order.value.asks = order.value.asks || [];
        order.value.asks.push({
            ask: "I approve this proof.",
            created_at: approvedAt,
            who: "customer",
        });

        show("Proof approved successfully. We will proceed with production.");
    } catch (e) {
        console.log(e)
    }
}

</script>

<template>
<div c2c-0 class="cc h-min:500">
    <div c2c-1 class="w-100%-max:1200">
        <div v-if="!isDraft && !isFromUrl" c2c-2 class="p-12 cc">
            <div c2c-3 class="w-100%-max:500 bg-0 sd bd">
                <p c2c-4 class="tb bg-0-l:-10 p-12">Search for an order.</p>
                <div c2c-5 class="p-12 m_end">
                    <web-input label="Order Id" v-model="orderId"></web-input>
                    <web-input label="Email" v-model="email"></web-input>
                    <button c2c-6 class="w-100% btl-blue" @click="getOrder">
                        View
                    </button>
                </div>
            </div>
        </div>
        <div c2c-7 class="p-12" v-if="order">

            <div c2c-8 class="rm rw m-b-12">
                <p v-if="!isDraft" c2c-9 class="tb fs-25">
                    Order detail #{{order._id}}
                </p>
                <p v-else c2c-10 class="tb fs-25">
                    View design
                </p>
                <p v-if="order.created_at" v-day="order.created_at"></p>
            </div>

            <div c2c-11 class="m-b-12 gr" v-if="order.status">
                <p c2c-12 class="inline-block"><span >Status:</span> <span c2c-13 class="tb upp bg-green-l:50 p-6-12 br-6">{{order.status}}</span></p>
            </div>
            <div c2c-14 class="m-b-12 gr" v-if="order.proof_approved_at">
                <p c2c-15 class="inline-block"><span>Proof approved at:</span> <span c2c-16 class="tb" v-day="[order.proof_approved_at, 'MM/DD/YYYY h:mm A']"></span></p>
            </div>
            <div v-if="canApproveProof" c2c-17 class="m-b-12 gr">
                <button c2c-18 class="btl-blue" @click="approveProof">
                    Approve Proof
                </button>
                <p c2c-19 class="fs-13 i m-t-8">
                    Click approve when this proof is ready for printing.
                </p>
            </div>

            <div v-if="order && order.tracking_id" c2c-20 class="gr m-t-12">
                <p c2c-21 class="tb c-green p-b-12">Your order has been shipped</p>
                <p>
                    Tracking#: {{order.tracking_id}}
                    <a c2c-22 class="bt-blue br-6 p-3-12" :href="`https://www.fedex.com/fedextrack/?trknbr=${order.tracking_id}`">Detail</a>
                </p>
            </div>


            <div c2c-23 class="gr m-t-12">
                <p c2c-24 class="tb fs-19">Order Discussion & Proof Approval</p>
                <p c2c-25 class="i">This is the discussion area for your order. Our designer will send you proofs here for review. You can also reply with any changes or questions.</p>
            </div>

            <div v-if="order && order.asks" c2c-26 class="chat-box m-t-12">
                <ul c2c-27 class="msg">
                    <li v-for="ask in order.asks">
                        <div v-if="ask.who === 'customer'" c2c-28 class="cr">
                            <div c2c-29 class="cr m-t-12 br-8 p-12-12 sd-0-0-5-2-g1a bg-0 ^ >3 w-max:80% >>img w-max:100% ^ <3 >>img w-max:100% h-auto">
                                <div c2c-30 class=">>img h-auto" v-html="ask.ask"></div>
                            </div>
                            <div c2c-31 class="fs-10 m-t-2 i c-0-l:-50 rm">
                                <p c2c-32 class="tb">Me</p>
                                <p c2c-33 class="m-l-6" v-day="ask.created_at"></p></div>
                        </div>
                        <div v-if="ask.who === 'admin'" c2c-34 class="cl">
                            <div c2c-35 class="m-t-12 br-8 p-12-12 sd-0-0-5-2-logo bg-0 ^ >3 w-max:80% >>img w-max:100% ^ <3 >>img w-max:100% h-auto">
                                <div c2c-36 class=">>img h-auto" v-html="ask.ask"></div>
                            </div>
                            <div c2c-37 class="fs-10 p-l-6 m-t-2 i c-0-l:-50 rm">
                                <p c2c-38 class="tb">Team Banner Sports</p>
                                <p c2c-39 class="m-l-12" v-day="ask.created_at"></p></div>
                        </div>
                    </li>
                </ul>
            </div>

            <div c2c-40 class="gr m-t-12">
                <web-editor v-model="ask" placeholder="Type your message here (e.g., approve proof, request changes…)"></web-editor>
                <button c2c-41 class="m-t-12 bt-blue" @click="submitAsk">Send Message</button>
            </div>



            <table c2c-42 class="bg-0 w-100% bd >>td bd p-12 ^ >>th p-12 bd">
                <thead>
                <tr>
                    <th></th>
                    <th>Information</th>
                    <th>Qty</th>
                    <th>Price</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, index) in order.items">
                    <td c2c-43 class="p-tb-12 cl">
                        <div c2c-44 class="block" v-if="item.item_type === 'tool_design'">
                            <a :href="`/design-tool?o=${order._id}&at=${index}`">
                                <div c2c-45 class="h-max:500" v-html="item.svg"></div>
                            </a>
                        </div>
                        <div v-if="item.item_type === 'order_form'">
                            <a :href="`/design-tool?u=${item.svg_url}`">
                                <img c2c-46 class="w-100%-max:300" :src="item.img_url" alt="">
                            </a>
<!--                            <div c2c-47 class="cc cm m-t-12" v-if="item._id" >-->
<!--                                <a c2c-48 class="btl-blue w-150" :href="`/team-banner/${item._id}`">Order Form</a>-->
<!--                            </div>-->

                        </div>


                        <div>
                            <div v-if="item.form_data">
                                <ul>
                                    <li v-for="(info, key) in item.form_data">
                                        <div v-if="key === <any>'players'">
                                            <ul>
                                                <li v-for="p in info">
                                                    {{p.name}} - <b>{{p.number}}</b>
                                                </li>
                                            </ul>
                                        </div>
                                        <p v-else>
                                            <span c2c-49 class="tb">{{_.startCase(key)}}: </span>
                                            <span>{{info}}</span>
                                        </p>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div v-if="item.option && (item.option.label === 'Mini banner' || item.option.label === 'Mini banners')" >
                            <p c2c-50 class="tb">Mini banners +$15.99</p>
                        </div>
                        <div c2c-51 class="c-red tb">
                            <p v-if="item.is_proof">Is Proof</p>
                            <p v-else>No Proof</p>
                        </div>

                    </td>
                    <td>
                        <div v-html="item.information"></div>
                    </td>
                    <td c2c-52 class="tc">
                        x {{item.qty}}
                    </td>
                    <td c2c-53 class="tc">
                        <div v-if="!isDraft">
                            ${{item.sale_price}}
                        </div>
                        <div v-else>
                            <p c2c-54 class="cr" v-if="item.sale_price === '99.99' || item.sale_price === 99.99">
                                <span c2c-55 class="lt">${{item.sale_price}}</span>
                                <span c2c-56 class="tb c-red">$69.99</span>
                            </p>
                            <p c2c-57 class="cr" v-if="item.sale_price === '13.99' || item.sale_price === 13.99">
                                <span c2c-58 class="lt">${{item.sale_price}}</span>
                                <span c2c-59 class="tb c-red">$3.99</span>
                            </p>
                        </div>
                    </td>
                </tr>
                <tr v-if="!isDraft">
                    <td colspan="4">
                        <p c2c-60 class="tr tb">
                            Subtotal: ${{order.subtotal}}
                        </p>
                    </td>
                </tr>
                <tr v-if="!isDraft">
                    <td colspan="4">
                        <p c2c-61 class="tr tb">
                            Ship cost: ${{order.ship_cost}}
                        </p>
                    </td>
                </tr>

                <tr v-if="!isDraft && order.discount">
                    <td colspan="4">
                        <p c2c-62 class="tr tb">
                            Discount: -${{order.discount}}
                        </p>
                    </td>
                </tr>

                <tr v-if="!isDraft">
                    <td colspan="4">
                        <p c2c-63 class="tr tb">
                            Tax: ${{order.tax}}
                        </p>
                    </td>
                </tr>
                <tr v-if="!isDraft">
                    <td colspan="4">
                        <p c2c-64 class="tr tb">
                            Total: ${{order.total}}
                        </p>
                    </td>
                </tr>
                </tbody>
            </table>

<!--            <div c2c-65 class="m-tb-12 rr rm">-->
<!--                <button c2c-66 class="btl-blue" @click="addDraftToCart">Add to cart</button>-->
<!--            </div>-->



            <div c2c-67 class="m-t-12 >2 col-2 ^ <2 col-1
                        ^ >div bd
                        ^ >>ul p-12
                        ^ >div>p bg-0-l:-10 p-12
                        ^ >2 >div:f bd-r-w:0">
                <div c2c-68 class="bg-0">
                    <p c2c-69 class="tb">Bill to</p>
                    <ul>
                        <li v-for="(value,key) in order.bill">
                            <p>
                                <span c2c-70 class="cap tb">{{_.startCase(key)}}</span>:
                                <span>{{value}}</span>
                            </p>
                        </li>
                    </ul>
                </div>

                <div c2c-71 class="bg-0 <2 m-t-12">
                    <p c2c-72 class="tb">Ship to</p>
                    <ul>
                        <li v-for="(value,key) in order.ship">
                            <p>
                                <span c2c-73 class="cap tb">{{_.startCase(key)}}</span>:
                                <span>{{value}}</span>
                            </p>
                        </li>
                    </ul>
                </div>
            </div>






        </div>
        <div v-else>
            <p c2c-74 class="tb tc">No orders found.</p>
        </div>
    </div>
</div>
</template>

<style>
.chat-box {
    .msg {
        img {
            height: auto !important;
            max-width: 100% !important;
        }
    }
}
</style>
<style lang="scss" scoped>.cc[c2c-0] {
                        display: flex; flex-direction: column; align-items: center;
                 }.h-min\:500[c2c-0] {
                        min-height: 500px;
                 }.w-100\%-max\:1200[c2c-1] {
                        width: 100%;max-width: 1200px;
                 }.p-12[c2c-2] {
                        padding-top: 12px;padding-right: 12px;padding-bottom: 12px;padding-left: 12px;
                 }.cc[c2c-2] {
                        display: flex; flex-direction: column; align-items: center;
                 }.w-100\%-max\:500[c2c-3] {
                        width: 100%;max-width: 500px;
                 }.bg-0[c2c-3] {
                        background-color: hsl(0, 0%, 100%);
                 }.sd[c2c-3] {
                        box-shadow: 0 1px 2px 0 rgba(0,0,0,0.2)
                 }.bd[c2c-3] {
                        border-width: 1px; border-style: solid; border-color: hsla(0, 0%, 72.5%, 0.7)
                 }.tb[c2c-4] {
                        font-weight: bold;
                 }.bg-0-l\:-10[c2c-4] {
                        background-color: hsl(0, 0%, 90%);
                 }.p-12[c2c-4] {
                        padding-top: 12px;padding-right: 12px;padding-bottom: 12px;padding-left: 12px;
                 }.p-12[c2c-5] {
                        padding-top: 12px;padding-right: 12px;padding-bottom: 12px;padding-left: 12px;
                 }.w-100\%[c2c-6] {
                        width: 100%;
                 }.p-12[c2c-7] {
                        padding-top: 12px;padding-right: 12px;padding-bottom: 12px;padding-left: 12px;
                 }.rm[c2c-8] {
                        display: flex; flex-direction: row; align-items: center;
                 }.rw[c2c-8] {
                        display: flex; flex-direction: row; justify-content: space-between;
                 }.m-b-12[c2c-8] {
                        margin-bottom: 12px;
                 }.tb[c2c-9] {
                        font-weight: bold;
                 }.fs-25[c2c-9] {
                        font-size: 25px;
                 }.tb[c2c-10] {
                        font-weight: bold;
                 }.fs-25[c2c-10] {
                        font-size: 25px;
                 }.m-b-12[c2c-11] {
                        margin-bottom: 12px;
                 }.inline-block[c2c-12] {
                        display: inline;
                 }.tb[c2c-13] {
                        font-weight: bold;
                 }.upp[c2c-13] {
                        text-transform: uppercase;
                 }.bg-green-l\:50[c2c-13] {
                        background-color: hsl(120, 100%, 75.1%);
                 }.p-6-12[c2c-13] {
                        padding-top: 6px;padding-right: 12px;padding-bottom: 6px;padding-left: 12px;
                 }.br-6[c2c-13] {
                        border-top-left-radius: 6px;border-top-right-radius: 6px;border-bottom-right-radius: 6px;border-bottom-left-radius: 6px;
                 }.m-b-12[c2c-14] {
                        margin-bottom: 12px;
                 }.inline-block[c2c-15] {
                        display: inline;
                 }.tb[c2c-16] {
                        font-weight: bold;
                 }.m-b-12[c2c-17] {
                        margin-bottom: 12px;
                 }.fs-13[c2c-19] {
                        font-size: 13px;
                 }.i[c2c-19] {
                        font-style: italic;
                 }.m-t-8[c2c-19] {
                        margin-top: 8px;
                 }.m-t-12[c2c-20] {
                        margin-top: 12px;
                 }.tb[c2c-21] {
                        font-weight: bold;
                 }.c-green[c2c-21] {
                        color: hsl(120, 100%, 25.1%);
                 }.p-b-12[c2c-21] {
                        padding-bottom: 12px;
                 }.br-6[c2c-22] {
                        border-top-left-radius: 6px;border-top-right-radius: 6px;border-bottom-right-radius: 6px;border-bottom-left-radius: 6px;
                 }.p-3-12[c2c-22] {
                        padding-top: 3px;padding-right: 12px;padding-bottom: 3px;padding-left: 12px;
                 }.m-t-12[c2c-23] {
                        margin-top: 12px;
                 }.tb[c2c-24] {
                        font-weight: bold;
                 }.fs-19[c2c-24] {
                        font-size: 19px;
                 }.i[c2c-25] {
                        font-style: italic;
                 }.m-t-12[c2c-26] {
                        margin-top: 12px;
                 }.cr[c2c-28] {
                        display: flex; flex-direction: column; align-items: flex-end;
                 }.cr[c2c-29] {
                        display: flex; flex-direction: column; align-items: flex-end;
                 }.m-t-12[c2c-29] {
                        margin-top: 12px;
                 }.br-8[c2c-29] {
                        border-top-left-radius: 8px;border-top-right-radius: 8px;border-bottom-right-radius: 8px;border-bottom-left-radius: 8px;
                 }.p-12-12[c2c-29] {
                        padding-top: 12px;padding-right: 12px;padding-bottom: 12px;padding-left: 12px;
                 }.sd-0-0-5-2-g1a[c2c-29] {
                        box-shadow: 0 0 5px 2px hsl(120deg 44.79% 38.99%)
                 }.bg-0[c2c-29] {
                        background-color: hsl(0, 0%, 100%);
                 }@media (min-width: 992px) {.\>3.w-max\:80\%[c2c-29] {
                        max-width: 80%;
                 }}@media (min-width: 992px) {.\>3.\>\>img.w-max\:100\%[c2c-29] img {
                        max-width: 100%;
                 }}@media (max-width: 992px) {.\<3.\>\>img.w-max\:100\%[c2c-29] img {
                        max-width: 100%;
                 }}@media (max-width: 992px) {.\<3.\>\>img.h-auto[c2c-29] img {
                        height: auto;
                 }}.\>\>img.h-auto[c2c-30] img {
                        height: auto;
                 }.fs-10[c2c-31] {
                        font-size: 10px;
                 }.m-t-2[c2c-31] {
                        margin-top: 2px;
                 }.i[c2c-31] {
                        font-style: italic;
                 }.c-0-l\:-50[c2c-31] {
                        color: hsl(0, 0%, 50%);
                 }.rm[c2c-31] {
                        display: flex; flex-direction: row; align-items: center;
                 }.tb[c2c-32] {
                        font-weight: bold;
                 }.m-l-6[c2c-33] {
                        margin-left: 6px;
                 }.cl[c2c-34] {
                        display: flex; flex-direction: column; align-items: flex-start;
                 }.m-t-12[c2c-35] {
                        margin-top: 12px;
                 }.br-8[c2c-35] {
                        border-top-left-radius: 8px;border-top-right-radius: 8px;border-bottom-right-radius: 8px;border-bottom-left-radius: 8px;
                 }.p-12-12[c2c-35] {
                        padding-top: 12px;padding-right: 12px;padding-bottom: 12px;padding-left: 12px;
                 }.sd-0-0-5-2-logo[c2c-35] {
                        box-shadow: 0 0 5px 2px hsl(197.42deg 95.88% 38.04%)
                 }.bg-0[c2c-35] {
                        background-color: hsl(0, 0%, 100%);
                 }@media (min-width: 992px) {.\>3.w-max\:80\%[c2c-35] {
                        max-width: 80%;
                 }}@media (min-width: 992px) {.\>3.\>\>img.w-max\:100\%[c2c-35] img {
                        max-width: 100%;
                 }}@media (max-width: 992px) {.\<3.\>\>img.w-max\:100\%[c2c-35] img {
                        max-width: 100%;
                 }}@media (max-width: 992px) {.\<3.\>\>img.h-auto[c2c-35] img {
                        height: auto;
                 }}.\>\>img.h-auto[c2c-36] img {
                        height: auto;
                 }.fs-10[c2c-37] {
                        font-size: 10px;
                 }.p-l-6[c2c-37] {
                        padding-left: 6px;
                 }.m-t-2[c2c-37] {
                        margin-top: 2px;
                 }.i[c2c-37] {
                        font-style: italic;
                 }.c-0-l\:-50[c2c-37] {
                        color: hsl(0, 0%, 50%);
                 }.rm[c2c-37] {
                        display: flex; flex-direction: row; align-items: center;
                 }.tb[c2c-38] {
                        font-weight: bold;
                 }.m-l-12[c2c-39] {
                        margin-left: 12px;
                 }.m-t-12[c2c-40] {
                        margin-top: 12px;
                 }.m-t-12[c2c-41] {
                        margin-top: 12px;
                 }.bg-0[c2c-42] {
                        background-color: hsl(0, 0%, 100%);
                 }.w-100\%[c2c-42] {
                        width: 100%;
                 }.bd[c2c-42] {
                        border-width: 1px; border-style: solid; border-color: hsla(0, 0%, 72.5%, 0.7)
                 }.\>\>td.bd[c2c-42] td {
                        border-width: 1px; border-style: solid; border-color: hsla(0, 0%, 72.5%, 0.7)
                 }.\>\>td.p-12[c2c-42] td {
                        padding-top: 12px;padding-right: 12px;padding-bottom: 12px;padding-left: 12px;
                 }.\>\>th.p-12[c2c-42] th {
                        padding-top: 12px;padding-right: 12px;padding-bottom: 12px;padding-left: 12px;
                 }.\>\>th.bd[c2c-42] th {
                        border-width: 1px; border-style: solid; border-color: hsla(0, 0%, 72.5%, 0.7)
                 }.p-tb-12[c2c-43] {
                        padding-top: 12px;padding-bottom: 12px;
                 }.cl[c2c-43] {
                        display: flex; flex-direction: column; align-items: flex-start;
                 }.block[c2c-44] {
                        display: block;
                 }.h-max\:500[c2c-45] {
                        max-height: 500px;
                 }.w-100\%-max\:300[c2c-46] {
                        width: 100%;max-width: 300px;
                 }.cc[c2c-47] {
                        display: flex; flex-direction: column; align-items: center;
                 }.cm[c2c-47] {
                        display: flex; flex-direction: column; justify-content: center;
                 }.m-t-12[c2c-47] {
                        margin-top: 12px;
                 }.w-150[c2c-48] {
                        width: 150px;
                 }.tb[c2c-49] {
                        font-weight: bold;
                 }.tb[c2c-50] {
                        font-weight: bold;
                 }.c-red[c2c-51] {
                        color: hsl(0, 100%, 50%);
                 }.tb[c2c-51] {
                        font-weight: bold;
                 }.tc[c2c-52] {
                        text-align: center;
                 }.tc[c2c-53] {
                        text-align: center;
                 }.cr[c2c-54] {
                        display: flex; flex-direction: column; align-items: flex-end;
                 }.lt[c2c-55] {
                        text-decoration: line-through;
                 }.tb[c2c-56] {
                        font-weight: bold;
                 }.c-red[c2c-56] {
                        color: hsl(0, 100%, 50%);
                 }.cr[c2c-57] {
                        display: flex; flex-direction: column; align-items: flex-end;
                 }.lt[c2c-58] {
                        text-decoration: line-through;
                 }.tb[c2c-59] {
                        font-weight: bold;
                 }.c-red[c2c-59] {
                        color: hsl(0, 100%, 50%);
                 }.tr[c2c-60] {
                        text-align: right;
                 }.tb[c2c-60] {
                        font-weight: bold;
                 }.tr[c2c-61] {
                        text-align: right;
                 }.tb[c2c-61] {
                        font-weight: bold;
                 }.tr[c2c-62] {
                        text-align: right;
                 }.tb[c2c-62] {
                        font-weight: bold;
                 }.tr[c2c-63] {
                        text-align: right;
                 }.tb[c2c-63] {
                        font-weight: bold;
                 }.tr[c2c-64] {
                        text-align: right;
                 }.tb[c2c-64] {
                        font-weight: bold;
                 }.m-tb-12[c2c-65] {
                        margin-top: 12px;margin-bottom: 12px;
                 }.rr[c2c-65] {
                        display: flex; flex-direction: row; justify-content: flex-end;
                 }.rm[c2c-65] {
                        display: flex; flex-direction: row; align-items: center;
                 }.m-t-12[c2c-67] {
                        margin-top: 12px;
                 }@media (min-width: 768px) {.\>2.col-2[c2c-67] {
                        
        display: flex;
        flex-flow: row wrap;
    
                > *:nth-child(2n+1) {
                    width: calc(100% / 2);
                }
            
                > *:nth-child(2n+2) {
                    width: calc(100% / 2);
                }
            
                 }}@media (max-width: 768px) {.\<2.col-1[c2c-67] {
                        
        display: flex;
        flex-flow: row wrap;
    
                > *:nth-child(1n+1) {
                    width: calc(100% / 1);
                }
            
                 }}.\>div.bd[c2c-67] > div {
                        border-width: 1px; border-style: solid; border-color: hsla(0, 0%, 72.5%, 0.7)
                 }.\>\>ul.p-12[c2c-67] ul {
                        padding-top: 12px;padding-right: 12px;padding-bottom: 12px;padding-left: 12px;
                 }.\>div\>p.bg-0-l\:-10[c2c-67] > div > p {
                        background-color: hsl(0, 0%, 90%);
                 }.\>div\>p.p-12[c2c-67] > div > p {
                        padding-top: 12px;padding-right: 12px;padding-bottom: 12px;padding-left: 12px;
                 }@media (min-width: 768px) {.\>2.\>div\:f.bd-r-w\:0[c2c-67] > div:first-child {
                        border-right-width: 0;
                 }}.bg-0[c2c-68] {
                        background-color: hsl(0, 0%, 100%);
                 }.tb[c2c-69] {
                        font-weight: bold;
                 }.cap[c2c-70] {
                        text-transform: capitalize;
                 }.tb[c2c-70] {
                        font-weight: bold;
                 }.bg-0[c2c-71] {
                        background-color: hsl(0, 0%, 100%);
                 }@media (max-width: 768px) {.\<2.m-t-12[c2c-71] {
                        margin-top: 12px;
                 }}.tb[c2c-72] {
                        font-weight: bold;
                 }.cap[c2c-73] {
                        text-transform: capitalize;
                 }.tb[c2c-73] {
                        font-weight: bold;
                 }.tb[c2c-74] {
                        font-weight: bold;
                 }.tc[c2c-74] {
                        text-align: center;
                 }</style>